import DataLoader from "@/components/DataLoader";
import Grid from "@/components/Grid";
import GridSupplementCard from "@/components/GridSupplementCard";
import Loading from "@/components/Loading";
import { SupplementsProvider, useSupplementsContext } from "@/contexts/SupplementContext";
import { AVAILABLE, OUT_OF_STOCK } from "@/core/constants";
import { SUPPLEMENT_DETAIL_QUERY } from "@/graphql/shop";
import Group from "@/tpo/Group";
import InfiniteScroll, { InfiniteScrollContext } from "@/tpo/InfiniteScroll";
import Modal from "@/tpo/Modal";
import SortMenu from "@/tpo/SortMenu";
import Stack from "@/tpo/Stack";
import { Search } from "@/tpo/TextInput";
import ButtonV2 from "@/v2/Buttons";

import ListPageTemplate from "./ListPageTemplate";
import { SupplementDetailContent } from "./SupplementDetailPage";

export function SupplementControls() {
  const { controls, location, history, search } = useSupplementsContext();

  return (
    <Stack gap={20} pb={[20, 20, 40]}>
      <Group
        gap={8}
        alignItems="center"
        justifyContent={["flex-start", "flex-start", "flex-end"]}
        flexWrap="wrap"
        width="100%"
      >
        <Search
          value={search}
          onChange={s => {
            const searchParams = new URLSearchParams(location.search);
            searchParams.set("search", s);
            history.push({
              path: location.path,
              search: searchParams.toString()
            });
          }}
          maxWidth={[null, null, 350]}
          minWidth={302}
          flex="0 1 auto"
        />
        <SortMenu
          value={controls.sort}
          setValue={controls.setSort}
          open={controls.sortMenuOpen}
          setOpen={controls.setSortMenuOpen}
          options={controls.sortOptions}
          placeholder="Sort By"
        />
      </Group>
    </Stack>
  );
}

export function SupplementList({ onAddToBasket, disabled = false }) {
  const { hasMore, loading, endCursor, fetchMore, supplements, setSelectedSupplement } =
    useSupplementsContext();

  return (
    <InfiniteScroll
      loader={<Loading />}
      hasMore={hasMore}
      loading={loading}
      next={() => {
        if (endCursor) {
          fetchMore({
            variables: {
              after: endCursor,
              first: 10
            }
          });
        }
      }}
      items={supplements}
    >
      <Grid
        gridTemplateColumns={[
          "repeat(1, 1fr)",
          "repeat(2, 1fr)",
          "repeat(2, 1fr)",
          "repeat(2, 1fr)",
          "repeat(3, 1fr)"
        ]}
        gap={16}
      >
        <InfiniteScrollContext.Consumer>
          {({ itemsList, setBottomElement }) => (
            <>
              {itemsList.map((supplement, idx) => (
                <GridSupplementCard.Card
                  data-component-name="SupplementCard"
                  key={supplement.id}
                  ref={itemsList.length - 1 === idx ? setBottomElement : null}
                  onClick={e => {
                    if (e.onAdd) return;
                    setSelectedSupplement(supplement.slug);
                  }}
                >
                  <GridSupplementCard.Header />
                  <Stack gap={16} pb={60} mt={20}>
                    <GridSupplementCard.Content
                      size={supplement.size}
                      shortDescription={supplement.description}
                      doseType={supplement.doseType}
                      dietryRestrictions={supplement.dietryRestrictions}
                      id={supplement.productCode}
                      name={supplement.name}
                      brandName={supplement.brandName}
                    />
                  </Stack>
                  <GridSupplementCard.PriceSection price={supplement.price}>
                    {supplement.status === OUT_OF_STOCK ? (
                      <ButtonV2 withChevron disabled color="midGrey" size={["sm", "sm", "md"]}>
                        out of stock
                      </ButtonV2>
                    ) : (
                      <ButtonV2
                        withChevron
                        color="green"
                        size={["sm", "sm", "md"]}
                        disabled={disabled}
                        onClick={e => {
                          if (supplement.status === AVAILABLE) {
                            e.onAdd = true;
                            onAddToBasket({ supplement });
                          }
                        }}
                      >
                        add
                      </ButtonV2>
                    )}
                  </GridSupplementCard.PriceSection>
                </GridSupplementCard.Card>
              ))}
            </>
          )}
        </InfiniteScrollContext.Consumer>
      </Grid>
    </InfiniteScroll>
  );
}

export function SupplementDetailModal({ onAddToBasket, disabled = false }) {
  const { selectedSupplement, setSelectedSupplement } = useSupplementsContext();

  return (
    <Modal
      maxWidth={1020}
      closeButton
      headerProps={{
        p: [20, 20, 40]
      }}
      bg="haze"
      show={!!selectedSupplement}
      close={() => setSelectedSupplement()}
      mode={["fullScreen", "fullScreen", "centered"]}
      data-component-name="SupplementDetailModal"
    >
      {selectedSupplement && (
        <DataLoader
          query={SUPPLEMENT_DETAIL_QUERY}
          variables={{
            slug: selectedSupplement
          }}
          render={({ supplement }) => (
            <SupplementDetailContent
              supplementIngredients={supplement.supplementIngredients}
              onAddToBasket={() => {
                setSelectedSupplement();
                onAddToBasket({ supplement });
              }}
              description={supplement.description}
              name={supplement.name}
              productImage={supplement.productImage}
              dietryRestrictions={supplement.dietryRestrictions}
              price={supplement.price}
              directions={supplement.directions}
              relatedUserResults={supplement.relatedUserResults}
              dimensions={{
                columnWidth: 600
              }}
              status={supplement.status}
              disabled={disabled}
            />
          )}
        />
      )}
    </Modal>
  );
}

export function SupplementListContent({
  bg,
  dietryRestrictions,
  types,
  brands,
  header,
  urlMap,
  title,
  subtitle,
  onAddToBasket,
  pb
}) {
  return (
    <SupplementsProvider dietryRestrictions={dietryRestrictions} types={types} brands={brands}>
      <ListPageTemplate
        switcherOptions={[
          {
            label: "tests",
            value: "tests"
          },
          {
            label: "supplements",
            value: "supplements"
          }
        ]}
        bg={bg}
        urlMap={urlMap}
        header={header}
        tab="supplements"
        title={title}
        subtitle={subtitle}
        pb={pb}
      >
        <SupplementControls />
        <SupplementList onAddToBasket={onAddToBasket} />
      </ListPageTemplate>
      <SupplementDetailModal onAddToBasket={onAddToBasket} />
    </SupplementsProvider>
  );
}
